import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import { useForm } from "react-hook-form";
import useFormField from "../hooks/useFormField";
import Button from "@mui/material/Button";
import React from "react";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import CollectionSyncSwitch from "../components/CollectionSyncSwitch";
import { COLLECTION_SYNC_MODES } from "../utils/collection";
import { describeApiError } from "../api/request";
import "../styles/collection.css";
const SYNC_DESCRIPTIONS = {
  ask: "Always ask prompts for each new change.",
  never: "Never applies collection changes only to BPM Binder.",
  always:
    "Always also updates Discogs: removing every local copy deletes every Discogs copy across folders. Partial removal reduces both counts. Restoration adds a Discogs copy only if none exists.",
};

export default function UserSettings({ settings, darkMode, setDarkMode }) {
  const form = useForm({
    defaultValues: {
      draft: null,
    },
    mode: "onChange",
  });
  const [draft, setDraft] = useFormField(form, "draft");
  const [saved, setSaved] = React.useState(false);
  const savePending = React.useRef(false);
  React.useEffect(() => {
    setDraft(null);
  }, [setDraft, settings.syncPreference]);
  const selected = draft ?? settings.syncPreference;
  const saveSelection = async (value) => {
    if (
      savePending.current ||
      settings.saving ||
      !COLLECTION_SYNC_MODES.includes(value) ||
      (!settings.saveError && value === settings.syncPreference)
    )
      return;
    savePending.current = true;
    setDraft(value);
    setSaved(false);
    try {
      if (await settings.save(value)) {
        setDraft(null);
        setSaved(true);
      }
    } finally {
      savePending.current = false;
    }
  };
  return (
    <section className="user-settings">
      <h2>User Settings</h2>
      <Paper
        component="section"
        variant="outlined"
        className="settings-section"
      >
        <h3>Collection</h3>
        <p>
          Sync collection changes with Discogs across sessions and devices. This
          affects future removals, quantity changes and restores. Saving this
          setting does not sync past changes.
        </p>
        {SYNC_DESCRIPTIONS[selected] && <p>{SYNC_DESCRIPTIONS[selected]}</p>}
        {settings.loading ? (
          <p role="status">Loading collection settings…</p>
        ) : settings.error ? (
          <Alert role="alert" severity="error">
            {describeApiError(settings.error)}{" "}
            <Button className="btn" onClick={settings.reload}>
              Retry settings
            </Button>
          </Alert>
        ) : (
          <>
            <CollectionSyncSwitch
              value={selected}
              disabled={settings.saving}
              onChange={saveSelection}
            />
            {settings.saveError && (
              <Alert role="alert" severity="error">
                {describeApiError(settings.saveError)}
              </Alert>
            )}
            {settings.saveError && (
              <Button
                className="btn"
                disabled={
                  settings.saving || !COLLECTION_SYNC_MODES.includes(selected)
                }
                onClick={() => saveSelection(selected)}
              >
                Retry saving settings
              </Button>
            )}
            {settings.saving && <p role="status">Saving collection setting…</p>}
            {saved && <p role="status">Collection setting saved.</p>}
          </>
        )}
      </Paper>
      <Paper
        component="section"
        variant="outlined"
        className="settings-section"
      >
        <h3>Appearance</h3>
        <FormControlLabel
          label="Dark Mode"
          control={
            <Switch
              checked={darkMode}
              disabled={!settings.canSaveAppearance}
              onChange={(_, checked) => setDarkMode(checked)}
              slotProps={{
                input: {
                  "aria-label": "Dark Mode",
                },
              }}
            />
          }
        />
        {settings.appearanceSaving && <p role="status">Saving appearance…</p>}
        {settings.appearanceError && (
          <Alert role="alert" severity="error">
            {describeApiError(settings.appearanceError)} Toggle Dark Mode to
            retry.
          </Alert>
        )}
      </Paper>
    </section>
  );
}
